import {
	Column,
	DataListRow,
	DrawerBody,
	FetchDrawer,
	FetchDrawerContent,
	FormattedDate,
	IDataListColumn,
	Id,
	SimpleDataList,
} from "@dgs/core";
import { isAfter } from "date-fns";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import { cancellationConfigItemService } from "../../../shared/api/cancellationConfigItemService";
import { CancellationConfigItemResource } from "../../../shared/types/cancellationConfigItem";

interface CancellationConfigItemsDrawerContentProps {
	title: string;
	onClose: () => void;
	items: CancellationConfigItemResource[];
}
const CancellationConfigItemsDrawerContent: FC<CancellationConfigItemsDrawerContentProps> = ({
	title,
	onClose,
	items,
}) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const activeItem = useMemo(() => {
		return items.findLast(({ startDate }) => isAfter(new Date(), startDate));
	}, [items]);

	const columns: IDataListColumn<CancellationConfigItemResource>[] = [
		{
			heading: t("Start date"),
			type: "value",
			valueKey: "startDate",
			render: ({ startDate }) => <FormattedDate date={startDate} />,
		},
		{
			heading: t("Cancellation fee"),
			type: "value",
			valueKey: "amountInPercent",
			render: ({ amountInPercent }) => t("{{amountInPercent}}% of the purchase price", { amountInPercent }),
		},
	];

	return (
		<FetchDrawerContent title={title} onClose={onClose}>
			<DrawerBody>
				<Column>
					<p>
						{t(
							"The cancellation conditions are listed below. The applicable deadlines and fees can be found in the table.",
						)}
					</p>
					<SimpleDataList
						$variant="outlined"
						columns={columns}
						isEmpty={items.length === 0}
						empty={t("No cancellation conditions available.")}
						data={items}
						renderRow={(row, index) => (
							<DataListRow
								index={index}
								backgroundColor={row.value === activeItem ? theme.colors.palette.grey[10] : undefined}
								row={row}
							/>
						)}
					/>
				</Column>
			</DrawerBody>
		</FetchDrawerContent>
	);
};

interface Props {
	open: boolean;
	onClose: () => void;
	shopItemId: Id;
}

export const CancellationConfigItemsDrawer: FC<Props> = ({ open, onClose, shopItemId }) => {
	const { t } = useTranslation();

	return (
		<FetchDrawer
			queryFn={() => cancellationConfigItemService.index(shopItemId).then((response) => response.data.data)}
			queryKey={cancellationConfigItemService.indexKey(shopItemId)}
			title={t("Cancellation conditions")}
			open={open}
			onClose={onClose}
			renderSuccess={({ data: items, ...props }) => <CancellationConfigItemsDrawerContent items={items} {...props} />}
		/>
	);
};
